/** Группы питания, их щитки и связность проложенных трасс. */
import { useForm } from "@tanstack/react-form";
import { useState } from "react";
import { z } from "zod";
import { getCatalogItem, isLowVoltagePanel, isPanel } from "@/shared/catalog";
import type { ElectricalGroup } from "@/shared/plan";
import { useProjectStore } from "@/shared/store";
import { useWiringStore } from "@/shared/store/layers";
import { UIKit } from "@/shared/ui";
import { electricalGroupConnectivity } from "@/widgets/electrical";
import { useEditorStore } from "@/widgets/editor";

const purposeOptions = [
	{ value: "outlets", label: "Розетки" },
	{ value: "lighting", label: "Освещение" },
	{ value: "dedicated", label: "Отдельный прибор" },
	{ value: "network", label: "Сеть LAN" },
	{ value: "tv", label: "Телевидение" },
	{ value: "other", label: "Другое" },
] as const;

const groupSchema = z.object({
	name: z.string().trim().min(1, "Укажите название").max(60),
	purpose: z.enum(["outlets", "lighting", "dedicated", "network", "tv", "other"]),
	panelId: z.string(),
});

const GroupEditor = ({ group, onClose }: { group: ElectricalGroup; onClose: () => void }) => {
	const recordEdit = useProjectStore((state) => state.recordEdit);
	const updateGroup = useWiringStore((state) => state.updateGroup);
	const form = useForm({
		defaultValues: { name: group.name, purpose: group.purpose },
		validators: {
			onChange: groupSchema.pick({ name: true, purpose: true }),
			onSubmit: groupSchema.pick({ name: true, purpose: true }),
		},
		onSubmit: ({ value }) => {
			recordEdit(() => updateGroup(group.id, { name: value.name.trim(), purpose: value.purpose }));
			onClose();
		},
	});
	return (
		<UIKit.Modal ariaLabel="Изменить группу питания" onClose={onClose} className="p-5 md:max-w-sm">
			<form className="flex flex-col gap-3" action={() => form.handleSubmit()}>
				<UIKit.FormField form={form} name="name">
					{(field) => (
						<UIKit.TextInput
							label="Название группы"
							value={field.state.value}
							onChange={field.handleChange}
							maxLength={60}
							autoFocus
						/>
					)}
				</UIKit.FormField>
				<UIKit.FormField form={form} name="purpose">
					{(field) => (
						<UIKit.Select
							label="Назначение"
							value={field.state.value}
							options={purposeOptions}
							onChange={field.handleChange}
						/>
					)}
				</UIKit.FormField>
				<UIKit.Button type="submit" className="px-3 py-2 text-sm">
					Сохранить
				</UIKit.Button>
			</form>
		</UIKit.Modal>
	);
};

export const WiringGroups = () => {
	const [editingId, setEditingId] = useState<string | null>(null);
	const layers = useProjectStore((state) => state.project.layers);
	const recordEdit = useProjectStore((state) => state.recordEdit);
	const groupId = useEditorStore((state) => state.cableGroupId);
	const setGroupId = useEditorStore((state) => state.setCableGroupId);
	const addGroup = useWiringStore((state) => state.addGroup);
	const updateGroup = useWiringStore((state) => state.updateGroup);
	const removeGroup = useWiringStore((state) => state.removeGroup);
	const panels = Object.values(layers.electrical.fixtures).filter((fixture) =>
		isPanel(getCatalogItem(fixture.ref)),
	);
	const lowVoltagePanels = Object.values(layers.electrical.fixtures).filter((fixture) =>
		isLowVoltagePanel(getCatalogItem(fixture.ref)),
	);
	const powerIds = new Set(panels.map((panel) => panel.id));
	const lowVoltageIds = new Set(lowVoltagePanels.map((panel) => panel.id));
	const panelIds = new Set([...powerIds, ...lowVoltageIds]);
	const panelOptions = [
		{ value: "", label: "Не выбран" },
		...panels.map((panel, index) => ({ value: panel.id, label: `Электрощит ${index + 1}` })),
		...lowVoltagePanels.map((panel, index) => ({
			value: panel.id,
			label: `Слаботочный узел ${index + 1}`,
		})),
	];
	const groups = Object.values(layers.wiring.groups);
	const unassigned = Object.values(layers.wiring.items).filter(
		(cable) => !cable.groupId || !layers.wiring.groups[cable.groupId],
	).length;
	const editingGroup = editingId ? layers.wiring.groups[editingId] : undefined;
	const form = useForm({
		defaultValues: {
			name: "",
			purpose: "outlets" as ElectricalGroup["purpose"],
			panelId: "",
		},
		validators: { onChange: groupSchema, onSubmit: groupSchema },
		onSubmit: ({ value }) => {
			const id = recordEdit(() =>
				addGroup({
					name: value.name.trim(),
					purpose: value.purpose,
					...(panelIds.has(value.panelId) ? { panelId: value.panelId } : {}),
				}),
			);
			setGroupId(id);
			form.reset();
		},
	});

	return (
		<details className="border-t border-border pt-2.5">
			<summary className="cursor-pointer text-xs font-semibold text-ink">
				<UIKit.Text as="span">Группы питания ({groups.length})</UIKit.Text>
			</summary>
			<section className="flex flex-col gap-2 pt-2">
				<UIKit.Text className="text-xs text-ink">Кабелей без группы: {unassigned}</UIKit.Text>
				<UIKit.Select
					label="Группа нового кабеля"
					value={groupId && layers.wiring.groups[groupId] ? groupId : ""}
					options={[
						{ value: "", label: "Не назначена" },
						...groups.map((group) => ({ value: group.id, label: group.name })),
					]}
					onChange={(value) => setGroupId(value || null)}
				/>
				{groups.map((group) => {
					const sourceIds =
						group.purpose === "network" || group.purpose === "tv" ? lowVoltageIds : powerIds;
					const status = electricalGroupConnectivity(group, layers.wiring.items, sourceIds);
					return (
						<div key={group.id} className="flex flex-col gap-1 border-t border-border pt-2">
							<div className="flex items-center justify-between gap-2">
								<UIKit.Button
									type="button"
									title="Изменить группу"
									className="min-w-0 text-left text-sm font-medium"
									onClick={() => setEditingId(group.id)}
								>
									{group.name}
								</UIKit.Button>
								<UIKit.Button
									type="button"
									title="Удалить группу, сохранив кабели"
									aria-label={`Удалить группу ${group.name}`}
									onClick={() => recordEdit(() => removeGroup(group.id))}
								>
									<UIKit.IconTrash />
								</UIKit.Button>
							</div>
							<UIKit.Select
								label="Источник"
								value={group.panelId && panelIds.has(group.panelId) ? group.panelId : ""}
								options={panelOptions}
								onChange={(value) =>
									recordEdit(() => updateGroup(group.id, { panelId: value || undefined }))
								}
							/>
							<UIKit.Text className="text-xs text-ink">
								{!status.panelAvailable
									? "Подходящий источник не назначен или удалён"
									: status.cableCount === 0
										? "Кабелей нет"
										: status.disconnectedCount
											? `Не доведено до источника: ${status.disconnectedCount} каб.`
											: "Все кабели группы связаны с источником"}
							</UIKit.Text>
						</div>
					);
				})}
				<form
					className="flex flex-col gap-2 border-t border-border pt-2"
					action={() => form.handleSubmit()}
				>
					<UIKit.FormField form={form} name="name">
						{(field) => (
							<UIKit.TextInput
								label="Название группы"
								value={field.state.value}
								onChange={field.handleChange}
								maxLength={60}
							/>
						)}
					</UIKit.FormField>
					<UIKit.FormField form={form} name="purpose">
						{(field) => (
							<UIKit.Select
								label="Назначение"
								value={field.state.value}
								options={purposeOptions}
								onChange={field.handleChange}
							/>
						)}
					</UIKit.FormField>
					<UIKit.FormField form={form} name="panelId">
						{(field) => (
							<UIKit.Select
								label="Источник"
								value={field.state.value}
								options={panelOptions}
								onChange={field.handleChange}
							/>
						)}
					</UIKit.FormField>
					<UIKit.Button type="submit" className="px-2 py-1.5 text-sm">
						Добавить группу
					</UIKit.Button>
				</form>
				{editingGroup && <GroupEditor group={editingGroup} onClose={() => setEditingId(null)} />}
			</section>
		</details>
	);
};
